import { AlertCircle, Box, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ModelWithFilesAndLinks } from '@maker-toolchest/shared';
import { ModelCard } from './ModelCard';

interface RecentModelsPanelProps {
  models: ModelWithFilesAndLinks[];
  isLoading?: boolean;
  error?: Error | null;
}

export function RecentModelsPanel({ models, isLoading, error }: RecentModelsPanelProps) {
  if (isLoading) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Recent Models</h2>
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-border bg-background-subtle p-4">
        <h2 className="mb-3 text-sm font-medium text-foreground">Recent Models</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
            <p className="text-sm text-foreground">Failed to load recent models</p>
            <p className="text-xs text-foreground-muted mt-1">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-border bg-background-subtle p-4">
      <h2 className="mb-3 text-sm font-medium text-foreground">Recent Models</h2>

      {models.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="mb-3 text-foreground-subtle">
            <Box className="h-10 w-10" />
          </div>
          <h3 className="mb-1 text-sm font-medium text-foreground">No models yet</h3>
          <p className="mb-4 max-w-sm text-xs text-foreground-muted">Add your first 3D model to get started</p>
          <Link
            to="/models"
            className="text-sm text-primary hover:underline"
          >
            Go to Models
          </Link>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {models.map((model) => (
              <ModelCard key={model.id} model={model} variant="compact" showRelativeTime />
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Link
              to="/models"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              View all models
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
